import style from "@/styles/pages/PropertyDetails/FinancingCondition.module.css";
import { FormProvider, useForm } from "react-hook-form";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "@/store/dtm";
import { DatePicker } from "@mui/x-date-pickers";
import { selectPropertyInfo, setFinancingConditionExists, setFinancingConditionDate } from "@/slices/propertyInfoSlice";
import dayjs from "dayjs";
import SlideTransition from "@/styles/animation/SlideTransition";
import { AddressDto, ApplicationDto, PropertyForCreationDto, SubjectPropertyDto } from "@/lib/DTMWebAPIClient.generated";
import { createApiClient } from "@/lib/Requests";
import { setApplicationProgress } from "@/slices/applicationSlice";
import { selectPropertyAddress } from "@/slices/propertyAddressSlice";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { applicationCategory } from "@/types/applicationType";
import { addBusinessDays, setClickInsightApplicationProgress, setClickInsightEvents, setClickInsightFormErrors } from "@/lib/util";

export default function FinancingCondition() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [financingCondition, setFinancingCondition] = useState("");
    const [financingConditionDateText, setFinancingConditionDateText] = useState<dayjs.Dayjs | null>(dayjs);
    const [displayError, setDisplayError] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const application = useSelector(
        (state: RootState) => state.application.application
    );
    const applicant = useSelector(
        (state: RootState) => state.applicant.applicant
    );
    const credential = useSelector(
        (state: RootState) => state.credential
    );
    const propertyInfo = useSelector(selectPropertyInfo).propertyInfo;
    const propertyAddress = useSelector(selectPropertyAddress).address;
    const referralProducts = useSelector((state: RootState) => state.referralProducts.products);
    const client = createApiClient({ timeout: 60000, token: credential.credential.accessToken });

    const methods = useForm({
        defaultValues: {
            financingCondition: "",
            financingConditionDate: ""
        },
    });

    useEffect(() => {
        document.title = "Declare Financing Conditions";
        setClickInsightEvents(referralProducts, undefined, undefined, undefined, undefined, application.id, applicant);

        methods.setValue("financingCondition", propertyInfo.financingConditionExists);
        setFinancingCondition(propertyInfo.financingConditionExists);
        methods.setValue("financingConditionDate", propertyInfo.financingConditionDate ? propertyInfo.financingConditionDate : "");
        if (propertyInfo.financingConditionDate){
            let dateComponents = propertyInfo.financingConditionDate.split("/");
            let date = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
            setFinancingConditionDateText(date);
        }

        methods.register("financingCondition", {
            required: "Please select a response.",
        });

        client.apiApplicationsFetch().then((result) => {
            result.propertyInfo = propertyInfo;
            result.application = application;
            client.apiApplicationsSave(new ApplicationDto({
                applicationData: JSON.stringify(result)
            })).catch((error) => {
                console.log("Error saving application info");
            });
        }).catch((error) => {
            console.log("Error fetching application info");
        });
    }, []);

    const onError = (errors: object) => {
        setClickInsightFormErrors(errors);
    }

    const financingConditionChangeHandler = (e: any) => {
        setFinancingCondition(e.target.value);
        methods.setValue("financingCondition", e.target.value);
        methods.clearErrors();

        switch (e.target.value) {
            case "yes":
                methods.register("financingConditionDate", {
                    required: "Please select a date",
                });
                setFinancingConditionDateText(null);
                break;
            case "no":
                methods.unregister("financingConditionDate");
                setDisplayError(false);
                break;
        }
    };

    const onSubmit = async (data: any) => {
        let selectedDate = undefined;
        let closingDate = undefined;
        if (propertyInfo.closingDate && propertyInfo.closingDate.trim() != "") {
            let dateComponents = propertyInfo.closingDate.split("/");
            closingDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
        }
        if (data.financingCondition === "yes") {
            let dateComponents = data.financingConditionDate.split("/");
            selectedDate = dayjs(dateComponents[2] + "-" + dateComponents[1] + "-" + dateComponents[0]);
            let maxDate = dayjs('2069-12-31');
            if (data.closingDate == "Invalid Date" || selectedDate > maxDate || selectedDate < addBusinessDays(0)) {
                setDisplayError(true);
                return;
            }
        }

        dispatch(setFinancingConditionExists(data.financingCondition));
        dispatch(setFinancingConditionDate(data.financingConditionDate));

        setIsSaving(true);
        await client.apiDealsApplicationsMortgagesSubjectProperty(new PropertyForCreationDto({
            applicationId: 0,
            remainingBalanceAmount: propertyInfo.mortgageBalance,
            downPaymentAmount: application.downPayment,
            totalLoanAmount: application.applicationCategory == applicationCategory.Buy ? propertyInfo.purchasePrice - application.downPayment : application.totalLoan,
            newMoneyRequestedAmount: 0,
            subjectProperty: new SubjectPropertyDto({
                originalPurchasePrice: propertyInfo.originalPurchasePrice,
                purchasePrice: application.applicationCategory == applicationCategory.Buy ? propertyInfo.purchasePrice : propertyInfo.propertyValue,
                propertySalePrice: 0,
                address: new AddressDto({
                    id: 0,
                    streetNumber: propertyAddress.streetNumber,
                    streetName: propertyAddress.streetName,
                    streetType: propertyAddress.streetType,
                    streetDirection: propertyAddress.streetDirection,
                    unitType: propertyAddress.unitType,
                    unitNumber: propertyAddress.unitNumber,
                    city: propertyAddress.city,
                    region: propertyAddress.province,
                    postalCode: propertyAddress.postalCode,
                    country: propertyAddress.country,
                    timeAtResidenceInMonths: 0,
                    residenceStatusType_Name: "",
                    deliveryMode: undefined,
                    modeNumber: undefined,
                    deliveryMode2: undefined,
                    modeNumber2: undefined,
                    isMailing: false
                }),
                propertyTax: propertyInfo.propertyTax,
                propertyTaxFrequency: "annually",
                mortgageHolder: propertyInfo.mortgageHolder,
                livableArea: propertyInfo.livableArea,
                livableAreaType: propertyInfo.livableAreaUnit,
                heatingCost: propertyInfo.heatingCost,
                heatingType: propertyInfo.heatingType,
                heatingCostFrequency: "monthly",
                condoFees: propertyInfo.maintenanceFee,
                condoFeesFrequency: "monthly",
                futureStatusOfProperty: "",
                occupancyType: propertyInfo.purposeOfProperty,
                subjectPropertyRentalUnits: propertyInfo.subjectPropertyRentalUnits,
                totalRentalUnits: propertyInfo.numOfRentalUnits,
                totalRentalProperties: propertyInfo.numOfRentalProperties,
                ownershipPercentage: propertyInfo.ownershipPercentage,
                hasCurrentRentalProperties: propertyInfo.ownOtherRentalProperties,
                isPlanningOnRenting: propertyInfo.rentOut,
                isSeparateLegalUnit: propertyInfo.separateUnit,
                rent: propertyInfo.monthlyRent,
                estimatedFundingDate: undefined,
                closingDate: closingDate,
                conditionOfFinancingDate: selectedDate,
                rentalIncome: propertyInfo.monthlyRent
            })
        })).then((result) => {
            dispatch(setApplicationProgress(16));
            setClickInsightApplicationProgress('Your ' + (application.applicationCategory == applicationCategory.Buy ? "new " : "") + 'home - How will you use this property?');
            navigate("/single/review-application");

        }).catch((error) => {

            console.log("Error saving property information", error);
            dispatch(setApplicationProgress(16));
            navigate("/single/review-application");
        });
    };

    return (
        <>
            <SlideTransition>
                <div className="flex flex-col max-xs:px-3 sm:ml-80 max-md:items-center max-md:mt-20">
                    <div
                        className={style.addHeading}
                        role="region"
                        aria-label="Address Header"
                    >
                        <h1 aria-label="Address Header">Does your offer have a financing condition?</h1>
                        <p className="sub-heading">When you make an offer on a home, you're able to make that offer conditional on being able to arrange financing by a certain date.</p>
                        {displayError &&
                            <div className={clsx("flex flex-row pcaautocomplete mt-5", style.errorContainer)}>
                                <img src="assets/error-info-icon.svg" alt="error" />
                                <span className="text-sm ml-2 mt-1">
                                    Please select a date on or after {dayjs(addBusinessDays(1)).format('LL')} (1 business day from today)
                                </span>
                            </div>
                        }
                        <div className={"flex flex-row mt-5"}>
                            <FormProvider {...methods}>
                                <form
                                    autoComplete="off"
                                    onSubmit={methods.handleSubmit(onSubmit, onError)}
                                >
                                    <div style={{ width: 350 }}>
                                        <div className="grid grid-flow-row sm:grid-flow-col mb-4">
                                            <ul className="grid grid-cols-3 gap-x-2 mt-2 max-w-md" style={{ maxWidth: "20rem" }}>
                                                <li className="relative">
                                                    <input
                                                        type="radio"
                                                        value="yes"
                                                        id="yes-partial-rent"
                                                        {...methods.register("financingCondition", {
                                                            onChange: financingConditionChangeHandler,
                                                        })}
                                                        checked={financingCondition === "yes"}
                                                        onChange={financingConditionChangeHandler}
                                                        className="sr-only peer "
                                                    />
                                                    <label
                                                        className={clsx(
                                                            style.phoneLabel,
                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                            methods.formState?.errors?.financingCondition?.message
                                                                ? style.labelError
                                                                : ""
                                                        )}
                                                        htmlFor="yes-partial-rent"
                                                    >
                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>Yes</p>
                                                    </label>
                                                </li>
                                                <li className="relative">
                                                    <input
                                                        type="radio"
                                                        value="no"
                                                        id="no-partial-rent"
                                                        {...methods.register("financingCondition", {
                                                            onChange: financingConditionChangeHandler,
                                                        })}
                                                        checked={financingCondition === "no"}
                                                        onChange={financingConditionChangeHandler}
                                                        className="sr-only peer leading-6"
                                                    />
                                                    <label
                                                        className={clsx(
                                                            style.phoneLabel,
                                                            "cursor-pointer focus:outline-none hover:bg-gray-100 peer-checked:ring-[--brand-blue] peer-checked:ring-2 peer-checked:border-transparent text-sm text-center",
                                                            methods.formState?.errors?.financingCondition?.message
                                                                ? style.labelError
                                                                : ""
                                                        )}
                                                        htmlFor="no-partial-rent"
                                                    >
                                                        <p className="p-2 brand-blue-text leading-6" style={{ lineHeight: "1.5rem" }}>No</p>
                                                    </label>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>

                                    {methods.formState?.errors?.financingCondition?.message && (
                                        <span className="flex items-center font-medium tracking-wide text-red-500 text-xs ml-1">
                                            {methods.formState.errors.financingCondition.message}
                                        </span>
                                    )}

                                    {financingCondition === "yes" &&
                                        <div className="flex md:flex-row flex-col w-max">
                                            <div className="">
                                                <label htmlFor="downPayment">What is the financing condition date?</label>
                                                <div className="flex relative">
                                                    <span>
                                                        <img
                                                            src="assets/error-icon.svg"
                                                            className={clsx(
                                                                methods.formState?.errors?.financingConditionDate?.message
                                                                    ? ""
                                                                    : "hidden",
                                                                "w-4 h-4 absolute top-6  max-xxs:ml-[210px] max-sm:ml-[300px]  xs:max-md:ml-[370px] md:ml-[195px]"
                                                            )}
                                                            alt="error"
                                                            height={5}
                                                            width={5}
                                                            style={{ top: "1.25rem", marginLeft: "230px" }}
                                                        />
                                                    </span>
                                                    <DatePicker
                                                        className={style.datePickerInput}
                                                        format="DD MMM YYYY"
                                                        minDate={addBusinessDays(1)}
                                                        value={financingConditionDateText}
                                                        onChange={(e) => {
                                                            if (e != undefined) {
                                                                methods.clearErrors();
                                                                methods.setValue("financingConditionDate", e.format('DD/MM/YYYY'));
                                                                setFinancingConditionDateText(dayjs(e.format('DD-MM-YYYY'), 'DD-MM-YYYY'));
                                                            }
                                                        }}
                                                        slotProps={{
                                                            textField: {
                                                                error: displayError,
                                                            },
                                                        }}
                                                        localeText={{
                                                            fieldMonthPlaceholder: (params) => (params.contentType === 'letter' ? 'MMM' : 'MM'),
                                                        }}
                                                    />
                                                </div>
                                                {methods.formState?.errors?.financingConditionDate?.message && (
                                                    <span className="flex items-center font-medium tracking-wide text-red-500 text-xs mt-1 ml-1">
                                                        {methods.formState?.errors?.financingConditionDate?.message.toString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    }

                                    <div className="flex max-sm:hidden">
                                        <div className="mb-5 mr-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-169 sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                Continue
                                            </button>
                                        </div>
                                    </div>
                                    <div className="sm:hidden">
                                        <div className="mb-5">
                                            <button
                                                type="submit"
                                                className="primary mt-5 w-full sm:w-28 h-11 px-6"
                                                disabled={isSaving}
                                            >
                                                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: 8 }} className={clsx(isSaving ? "" : "hidden")} />
                                                Continue
                                            </button>
                                        </div>
                                        <div className="mb-5">
                                            <button onClick={() => navigate(-1)}
                                                type="button"
                                                className="back mt-5 w-full sm:w-28 h-11 px-6">
                                                Back
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                </div>
            </SlideTransition>
        </>
    );
}